import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Clock3,
  Cpu,
  FileSignature,
  FolderKanban,
  Newspaper,
  PenLine,
  Receipt,
  ShieldAlert,
  Sparkles,
} from 'lucide-react';
import type { LucideProps } from 'lucide-react';
import type { ComponentType } from 'react';
import { PageHeader } from '@/components/page-header';
import { Card, CardBody, CardHeader } from '@/components/card';
import { EmptyState } from '@/components/empty-state';
import { useMe } from '@/lib/auth';
import { useHasPermission } from '@/lib/permissions';
import { usePortalDashboard } from '@/lib/portal-dashboard';
import type { PortalDashboardData } from '@/types/api';

function greeting(): string {
  const h = new Date().getHours();
  if (h < 5) return 'Working late';
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

function formatToday(): string {
  return new Date().toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * Client-facing portal home. Shows only what the client's enabled features
 * warrant — no internal business metrics. Distinct from the admin Dashboard.
 */
export function PortalDashboard() {
  const me = useMe();
  const { data, isLoading } = usePortalDashboard();

  const hasProjects = useHasPermission('projects');
  const hasBlog = useHasPermission('blog');
  const hasAssets = useHasPermission('assets');
  const hasProposals = useHasPermission('proposals.view');
  const hasInvoices = useHasPermission('invoices.view');
  const anyFeature = hasProjects || hasBlog || hasAssets || hasProposals || hasInvoices;

  const firstName = me.data ? me.data.name.split(' ')[0] : '';

  return (
    <div className="space-y-6">
      <PageHeader title={`${greeting()}${firstName ? `, ${firstName}` : ''}.`} subtitle={formatToday()} />

      {!anyFeature ? (
        <EmptyState
          icon={Sparkles}
          title="Welcome to your portal"
          description="This is your space with the Mehrutherm team. As we set things up, your projects, content, and IT details will appear here."
        />
      ) : (
        <>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {hasProposals ? (
              <KpiTile
                label="Open proposals"
                value={isLoading ? '—' : String(data?.proposals_open ?? 0)}
                sub={data && data.proposals_open > 0 ? 'awaiting your response' : undefined}
                icon={FileSignature}
                link="/proposals"
                accent={!!data && data.proposals_open > 0}
              />
            ) : null}
            {hasInvoices ? (
              <KpiTile
                label="Invoices due"
                value={isLoading ? '—' : String(data?.invoices_due ?? 0)}
                sub={data && data.invoices_due > 0 ? 'awaiting payment' : undefined}
                icon={Receipt}
                link="/invoices"
                accent={!!data && data.invoices_due > 0}
              />
            ) : null}
            {hasProjects ? (
              <KpiTile
                label="Active projects"
                value={isLoading ? '—' : String(data?.projects_active ?? 0)}
                icon={FolderKanban}
                link="/projects"
              />
            ) : null}
            {hasBlog ? (
              <KpiTile
                label="Live posts"
                value={isLoading ? '—' : String(data?.blog_live ?? 0)}
                sub={blogSub(data)}
                icon={Newspaper}
                link="/blog"
                accent
              />
            ) : null}
            {hasAssets ? (
              <KpiTile
                label="IT assets"
                value={isLoading ? '—' : String(data?.assets_total ?? 0)}
                sub={
                  data && data.assets_warranty_soon > 0
                    ? `${data.assets_warranty_soon} warranty expiring soon`
                    : undefined
                }
                icon={Cpu}
                link="/assets"
              />
            ) : null}
          </div>

          <div className="grid grid-cols-1 gap-5 xl:grid-cols-3">
            <div className="xl:col-span-2">
              <NeedsAttention
                data={data}
                hasBlog={hasBlog}
                hasAssets={hasAssets}
                hasProposals={hasProposals}
                hasInvoices={hasInvoices}
              />
            </div>
            <QuickLinks hasProjects={hasProjects} hasBlog={hasBlog} hasAssets={hasAssets} />
          </div>
        </>
      )}
    </div>
  );
}

function blogSub(data?: PortalDashboardData): string | undefined {
  if (!data) return undefined;
  const parts: string[] = [];
  if (data.blog_drafts > 0) parts.push(`${data.blog_drafts} draft${data.blog_drafts === 1 ? '' : 's'}`);
  if (data.blog_submitted > 0) parts.push(`${data.blog_submitted} submitted`);
  return parts.length ? parts.join(' · ') : undefined;
}

function KpiTile({
  label,
  value,
  sub,
  icon: Icon,
  link,
  accent,
}: {
  label: string;
  value: string;
  sub?: string;
  icon: ComponentType<LucideProps>;
  link: string;
  accent?: boolean;
}) {
  return (
    <Link to={link} className="block">
      <div
        className={`group/k rounded-xl border bg-card p-4 shadow-xs transition-colors hover:border-primary/40 ${
          accent ? 'border-primary/20' : 'border-border/70'
        }`}
      >
        <div className="flex items-center justify-between gap-2">
          <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
            {label}
          </span>
          <Icon className={`size-4 ${accent ? 'text-primary' : 'text-muted-foreground'}`} />
        </div>
        <div className="mt-1.5 text-2xl font-semibold tabular-nums tracking-tight">{value}</div>
        <div className="mt-0.5 text-xs text-muted-foreground">{sub ?? ' '}</div>
      </div>
    </Link>
  );
}

function NeedsAttention({
  data,
  hasBlog,
  hasAssets,
  hasProposals,
  hasInvoices,
}: {
  data?: PortalDashboardData;
  hasBlog: boolean;
  hasAssets: boolean;
  hasProposals: boolean;
  hasInvoices: boolean;
}) {
  const items: Array<{ icon: ComponentType<LucideProps>; text: string; to: string }> = [];
  if (hasProposals && data && data.proposals_open > 0) {
    items.push({
      icon: FileSignature,
      text: `${data.proposals_open} proposal${data.proposals_open === 1 ? '' : 's'} awaiting your response`,
      to: '/proposals',
    });
  }
  if (hasInvoices && data && data.invoices_due > 0) {
    items.push({
      icon: Receipt,
      text: `${data.invoices_due} invoice${data.invoices_due === 1 ? '' : 's'} due for payment`,
      to: '/invoices',
    });
  }
  if (hasBlog && data && data.blog_submitted > 0) {
    items.push({
      icon: Clock3,
      text: `${data.blog_submitted} post${data.blog_submitted === 1 ? '' : 's'} awaiting review by the Mehrutherm team`,
      to: '/blog?status=submitted',
    });
  }
  if (hasAssets && data && data.assets_warranty_soon > 0) {
    items.push({
      icon: ShieldAlert,
      text: `${data.assets_warranty_soon} asset warrant${data.assets_warranty_soon === 1 ? 'y' : 'ies'} expiring within 60 days`,
      to: '/assets',
    });
  }

  return (
    <Card>
      <CardHeader>
        <h2 className="text-sm font-semibold">Needs your attention</h2>
      </CardHeader>
      <CardBody className="!p-0">
        {items.length === 0 ? (
          <div className="px-5 py-10 text-center">
            <p className="text-sm font-medium">You're all caught up</p>
            <p className="mt-1 text-sm text-muted-foreground">Nothing needs your attention right now.</p>
          </div>
        ) : (
          <ul className="divide-y divide-[var(--color-border)]">
            {items.map((item, i) => {
              const Icon = item.icon;
              return (
                <li key={i}>
                  <Link
                    to={item.to}
                    className="flex items-center gap-3 px-5 py-3.5 transition-colors hover:bg-muted/40"
                  >
                    <div className="inline-flex size-8 shrink-0 items-center justify-center rounded-lg bg-[var(--color-surface-muted)] text-[var(--color-foreground-muted)]">
                      <Icon className="size-4" />
                    </div>
                    <span className="flex-1 text-sm">{item.text}</span>
                    <ArrowRight className="size-4 text-muted-foreground" />
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}

function QuickLinks({
  hasProjects,
  hasBlog,
  hasAssets,
}: {
  hasProjects: boolean;
  hasBlog: boolean;
  hasAssets: boolean;
}) {
  const links: Array<{ icon: ComponentType<LucideProps>; label: string; to: string }> = [];
  if (hasBlog) links.push({ icon: PenLine, label: 'Write a post', to: '/blog/new' });
  if (hasProjects) links.push({ icon: FolderKanban, label: 'View projects', to: '/projects' });
  if (hasAssets) links.push({ icon: Cpu, label: 'View IT assets', to: '/assets' });

  if (links.length === 0) return null;

  return (
    <Card>
      <CardHeader>
        <h2 className="text-sm font-semibold">Quick links</h2>
      </CardHeader>
      <CardBody className="grid grid-cols-1 gap-2">
        {links.map((link) => {
          const Icon = link.icon;
          return (
            <Link
              key={link.to}
              to={link.to}
              className="flex items-center gap-2.5 rounded-md border border-[var(--color-border)] px-3 py-2 text-sm transition-colors hover:border-primary/40 hover:bg-muted/40"
            >
              <Icon className="size-4 text-muted-foreground" />
              {link.label}
            </Link>
          );
        })}
      </CardBody>
    </Card>
  );
}
